import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { arrayAdd, arrayRemove, arrayUpdate } from '@datorama/akita';
import intersect from '@turf/intersect';
import { deepCopy } from 'deep-copy-ts';
import { Feature, Polygon } from 'geojson';
import { Observable, of } from 'rxjs';
import { finalize, map, tap } from 'rxjs/operators';
import { JsonSchemaService } from 'src/app/core/json-schema/json-schema.service';
import { CostElement } from 'src/app/cost-elements/model/cost-element';
import { environment } from 'src/environments/environment';
import { ProjectQuery } from '../../projects/state/project.query';
import {
  AttendanceBox, CableExternal, CaixasDeAtendimentoHorizontalBalanceado, CaixasDeAtendimentoVertical, MitraNetworkParameters,
  SecoesDeSplitter, Splitter
} from './network-parameters.model.generated';
import {
  CheckSimulatioArea, createSimulation, Simulation, SimulationAreaResult, SimulationParams
} from './simulation.model';
import { SimulationQuery } from './simulation.query';
import { SimulationStore } from './simulation.store';

@Injectable({ providedIn: 'root' })
export class SimulationService {

  constructor(
    private simulationStore: SimulationStore,
    private simulationQuery: SimulationQuery,
    private projectQuery: ProjectQuery,
    private http: HttpClient,
    private jsonSchemaService: JsonSchemaService
  ) {
  }

  get() {
    this.simulationStore.setLoading(true);
    return this.http.get<Simulation[]>(`/api/project/${this.projectQuery.getActiveId()}/simulation`)
      .pipe(
        tap(simulations => {
          this.simulationStore.set(simulations);
        }),
        finalize(() => {
          this.simulationStore.setLoading(false);
        })
      );
  }

  getById(id: number) {
    return this.http.get<Simulation>(`/api/project/${this.projectQuery.getActiveId()}/simulation/${id}`)
      .pipe(map(s => {
        this.validateParametersSchema(deepCopy(s.parameters));
        this.preparToFrontEnd(s);
        this.simulationStore.update(s.id, s);
        return s;
      }));
  }

  add(simulation: Simulation) {
    return this.http.post<Simulation>(`/api/project/${this.projectQuery.getActiveId()}/simulation`, simulation)
      .pipe(tap(s => {
        this.simulationStore.add(s);
      }));
  }

  duplicate(simulation: Simulation) {
    const newSimulation = createSimulation({
      ...simulation,
      name: simulation.name + ' COPY',
      status: undefined
    });
    return this.add(newSimulation);
  }

  update(id: number, simulation: Partial<Simulation>) {
    const copy = deepCopy(simulation);
    this.preparToBackEnd(copy);
    this.validateParametersSchema(copy.parameters);

    return this.http.put<Simulation>(`/api/project/${this.projectQuery.getActiveId()}/simulation/${id}`, copy)
      .pipe(tap(s => {
        this.preparToFrontEnd(s);
        return this.simulationStore.update(id, s);
      }));
  }

  remove(id: number) {
    if (typeof id === 'number') {
      return this.http.delete(`/api/project/${this.projectQuery.getActiveId()}/simulation/${id}`)
        .pipe(tap(s => {
          this.simulationStore.remove(id);
        }));
    }

    this.simulationStore.remove(id);
    return of();
  }

  clear() {
    this.simulationStore.remove();
  }

  active(id: number) {
    this.simulationStore.setActive(id);
  }

  desactive() {
    this.active(null);
    this.setCoverageMode(false);
  }

  addArea(id: number, simulationArea: Feature<Polygon>, params: SimulationParams) {
    if (!simulationArea.id) {
      throw new Error('Feature id must not be empty');
    }
    this.simulationStore.update(id, ({ areas_definition }) => ({
      areas_definition: arrayAdd(areas_definition, simulationArea)
    }));

    return this.updateAttendanceRates(id, params);
  }

  updateArea(id: number, simulationArea: Feature<Polygon>, params: SimulationParams) {
    this.simulationStore.update(id, ({ areas_definition }) => ({
      areas_definition: arrayUpdate(
        areas_definition,
        (a) => a.id === simulationArea.id,
        simulationArea
      )
    }));

    return this.updateAttendanceRates(id, params);
  }

  removeArea(id: number, areaFeatureId: string, params: SimulationParams) {
    this.simulationStore.update(id, ({ areas_definition }) => ({
      areas_definition: arrayRemove(
        areas_definition,
        (a) => a.id === areaFeatureId
      )
    }));

    return this.updateAttendanceRates(id, params);
  }

  checkIntersectionAreas(simulation: Simulation): boolean {
    const { areas_definition } = simulation;

    return areas_definition.some(areaA => {
      return areas_definition.find(areaB => {
        if (areaA === areaB) {
          return false;
        }
        return intersect(areaA, areaB);
      });
    });
  }

  checkSimulationAreas(check: CheckSimulatioArea) {
    return this.http.post<SimulationAreaResult>(
      `/api/project/${this.projectQuery.getActiveId()}/check-simulation-areas`,
      check
    );
  }

  validateSimulation(simulation: Simulation) {
    const copy = deepCopy(simulation);
    this.preparToBackEnd(copy);

    return this.http.post<Simulation>(
      `/api/project/${this.projectQuery.getActiveId()}/simulation/${simulation.id}/validate`,
      copy
    );
  }

  execute(id: number) {
    return this.http.post<Simulation>(`/api/project/${this.projectQuery.getActiveId()}/simulation/${id}/execute`, {})
      .pipe(tap(s => {
        return this.simulationStore.update(id, s);
      }));
  }

  compare(simulations: number[]) {
    return this.http.post<any>(
      `/api/project/${this.projectQuery.getActiveId()}/simulation-comparison`,
      { simulations }
    );
  }

  setResultMode(resultMode: boolean) {
    this.simulationStore.update({ resultMode });
  }

  setCoverageMode(coverageMode: boolean) {
    this.simulationStore.update({ coverageMode });
  }

  getMaterialList(id: number): Observable<CostElement[]> {
    return this.http.get<CostElement[]>(
      `/api/project/${this.projectQuery.getActiveId()}/simulation/${id}/material-list-unit`,
    );
  }

  hasUnbalanced(params: MitraNetworkParameters): boolean {
    if (params.horizontal_settings?.network_type === 'unbalanced') {
      return true;
    }

    if (params.vertical_settings) {
      const unbalanced = params.vertical_settings.types.some(type => type.configuration.network_type === 'unbalanced');
      if (unbalanced) {
        return true;
      }
    }

    if (params.horizontal_collective_settings) {
      const unbalanced = params.horizontal_collective_settings.types.some(type => type.configuration.network_type === 'unbalanced');
      if (unbalanced) {
        return true;
      }
    }
  }

  hasVerticalBalanced(params: MitraNetworkParameters): boolean {
    if (!params.vertical_settings) {
      return false;
    }
    return params.vertical_settings.types.some(type => type.configuration.network_type === 'balanced');
  }

  hasVerticalUnBalanced(params: MitraNetworkParameters): boolean {
    if (!params.vertical_settings) {
      return false;
    }
    return params.vertical_settings.types.some(type => type.configuration.network_type === 'unbalanced');
  }

  isHorizontalBalanced(params: MitraNetworkParameters): boolean {
    if (!params.horizontal_settings) {
      return false;
    }
    return params.horizontal_settings.network_type === 'balanced';
  }

  isHorizontalUnbalanced(params: MitraNetworkParameters): boolean {
    if (!params.horizontal_settings) {
      return false;
    }
    return params.horizontal_settings.network_type === 'unbalanced';
  }

  getSplitters(params: MitraNetworkParameters): Splitter[] {
    const splitters: Splitter[] = [];

    const { horizontal_settings } = params;

    if (horizontal_settings.attendance_splitter >= 0 && horizontal_settings.network_type === 'balanced') {
      splitters.push(horizontal_settings.attendance_splitter);
    }

    horizontal_settings.splitter_sections
      .forEach(s => {

        if (!splitters.includes(s.splitter)) {
          splitters.push(s.splitter);
        }
      });

    params.vertical_settings.types
      .forEach(t => {

        t.configuration.splitter_sections
          .forEach(s => {

            if (!splitters.includes(s.splitter)) {
              splitters.push(s.splitter);
            }
          });
      });

    // if (this.hasVerticalUnBalanced(params) && !splitters.includes(VERTICAL_UNBALANCED_ATTENDANCE_SPLITTER)) {
    //   splitters.push(VERTICAL_UNBALANCED_ATTENDANCE_SPLITTER);
    // }

    return splitters;
  }

  clearParametersErrors() {
    this.simulationStore.update({ parametersErrors: [] });
  }

  getSimulationParams() {
    const projectId = this.projectQuery.getActiveId();
    return this.http.get<SimulationParams>(`/api/project/${projectId}/simulation-default-params`);
  }

  private validateParametersSchema(params: MitraNetworkParameters) {

    if (environment.production) {
      return;
    }

    this.jsonSchemaService.validate('/assets/schema/mitra-design-config.json', params)
      .subscribe(parametersErrors => {
        this.simulationStore.update({ parametersErrors });
      });
  }

  private preparToFrontEnd(simulation: Partial<Simulation>) {
    const { parameters } = simulation;

    if (!parameters.horizontal_settings.splitter_sections) {
      const value: Partial<SecoesDeSplitter> = [];
      parameters.horizontal_settings.splitter_sections = value as SecoesDeSplitter;
    }

    if (this.hasVerticalBalanced(parameters) && !parameters.vertical_attendance_cable_types.length) {
      parameters.vertical_attendance_cable_types.push({} as CableExternal);
    }

    if (!parameters.attendance_box_types_horiz) {
      const value: Partial<CaixasDeAtendimentoHorizontalBalanceado> = [];
      parameters.attendance_box_types_horiz = value;
    }

    if (!parameters.attendance_box_types_vert) {
      const value: Partial<CaixasDeAtendimentoVertical> = [];
      parameters.attendance_box_types_vert = value as CaixasDeAtendimentoVertical;
    }
  }

  private preparToBackEnd(simulation: Partial<Simulation>) {
    const { parameters } = simulation;


    if (!parameters.horizontal_settings.splitter_sections.length) {
      parameters.horizontal_settings.splitter_sections = undefined;
    }

    if (this.isHorizontalBalanced(parameters)) {
      if (!parameters.attendance_box_types_horiz.length) {
        const value = {} as AttendanceBox;
        parameters.attendance_box_types_horiz.push(value);
      }
    } else {
      const value: Partial<CaixasDeAtendimentoHorizontalBalanceado> = [];
      parameters.attendance_box_types_horiz = value;
    }

    if (!parameters.attendance_box_types_vert.length) {
      parameters.attendance_box_types_vert = undefined;
    }
  }

  private updateAttendanceRates(id: number, params: SimulationParams) {
    const simulation = createSimulation(this.simulationQuery.getEntity(id));
    const { demographicAreas } = this.projectQuery.getUi(this.projectQuery.getActiveId());

    const economicClasses: string[] = this.findEconomicClassesIntersections(simulation, demographicAreas);

    const rates = simulation.attendance_rates
      .filter(rate => economicClasses.includes(rate.economic_class));

    economicClasses
      .filter(economicClass => !rates.some(rate => rate.economic_class === economicClass))
      .forEach(economicClass => {
        const rate = params.simulation_attendance_rates_default.find(p => p.economic_class === economicClass);
        rates.push(rate);
      });

    simulation.attendance_rates = rates;
    return this.update(id, simulation);
  }


  private findEconomicClassesIntersections(simulation: Simulation, demographicAreas) {
    const economicClasses: string[] = [];

    simulation.areas_definition.forEach(area => {

      demographicAreas.features.forEach(demographic => {
        const intersection = intersect(area, demographic);
        if (intersection && !economicClasses.includes(demographic.properties.class)) {
          economicClasses.push(demographic.properties.class);
        }
      });
    });

    return economicClasses;
  }
}

export const VERTICAL_UNBALANCED_ATTENDANCE_SPLITTER = 8;
